/**
 * Gerenciador de sessão para rastreamento de impressões
 * Gera e mantém um session_id único por sessão do usuário
 */

const SESSION_KEY = 'connect_session_id';
const TIMEOUT_KEY = 'connect_session_timeout';
const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutos em milissegundos

/**
 * Gera um UUID v4 único
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Obtém o session_id atual ou cria um novo se não existir ou estiver expirado
 */
export function getSessionId(): string {
  const now = Date.now();
  const storedSessionId = localStorage.getItem(SESSION_KEY);
  const storedTimeout = localStorage.getItem(TIMEOUT_KEY);

  // Verifica se existe uma sessão válida
  if (storedSessionId && storedTimeout) {
    const timeout = parseInt(storedTimeout, 10);
    
    // Se não expirou, atualiza o timeout e retorna o session_id existente
    if (now < timeout) {
      updateTimeout();
      return storedSessionId;
    }
  }

  // Cria uma nova sessão
  const newSessionId = generateUUID();
  localStorage.setItem(SESSION_KEY, newSessionId);
  updateTimeout();
  
  return newSessionId;
}

/**
 * Atualiza o timeout da sessão atual
 */
function updateTimeout(): void {
  const newTimeout = Date.now() + TIMEOUT_DURATION;
  localStorage.setItem(TIMEOUT_KEY, newTimeout.toString());
}

/**
 * Força a renovação da sessão (cria um novo session_id)
 */
export function renewSession(): string {
  const newSessionId = generateUUID();
  localStorage.setItem(SESSION_KEY, newSessionId);
  updateTimeout();
  
  return newSessionId;
}

/**
 * Limpa a sessão atual
 */
export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(TIMEOUT_KEY);
}

/**
 * Verifica se há uma sessão ativa
 */
export function hasActiveSession(): boolean {
  const storedTimeout = localStorage.getItem(TIMEOUT_KEY);
  
  if (!storedTimeout) {
    return false;
  }
  
  const timeout = parseInt(storedTimeout, 10);
  return Date.now() < timeout;
}

/**
 * Hook para monitorar atividade do usuário e renovar timeout
 */
export function initSessionMonitoring(): void {
  // Atualiza o timeout em eventos de atividade do usuário
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  
  let activityTimeout: NodeJS.Timeout | null = null;
  
  const handleActivity = () => {
    // Usa debounce para não atualizar a cada evento
    if (activityTimeout) {
      clearTimeout(activityTimeout);
    }
    
    activityTimeout = setTimeout(() => {
      if (hasActiveSession()) {
        updateTimeout();
      }
    }, 1000); // Atualiza no máximo a cada segundo
  };
  
  activityEvents.forEach(event => {
    window.addEventListener(event, handleActivity, { passive: true });
  });
}

